// src/encounter/lib/lab-set-catalogue.ts
//
// The LAB catalogue lab chart links quote. `labs.set:<set id>` quotes a whole panel; `labs.<line id>`
// quotes one test (e.g. `labs.hba1c`, `labs.potassium`) — the ids agreed with the adult primary
// care phrase library (PR #615). The catalogue says which analytes a panel holds, in which
// order, by which LOINC codes, and which of the laboratory's own fields each line carries. DATA, in
// `lab-set-catalogue-v1.json` (versioned; its `basis` and `loincVerified` say where it came from).
// Lipids is one entry among many; the resolver and the card know nothing about any panel.
//
// A line quotes the latest CURRENT result of ANY of its codes — the codes of one analyte (mass and
// molar units, the common methods), never two analytes. `carry` names the lab's own fields the line
// takes into the note: `flag` (the interpretation, verbatim) and `range` (the printed reference
// range). Nothing is computed from either.

import catalogue from './lab-set-catalogue-v1.json';

export type CarriedField = 'flag' | 'range';

export interface LabSetLine {
  id: string;
  /** What the analyte is called when the record gives no name of its own. */
  label: string;
  loinc: readonly string[];
  carry: readonly CarriedField[];
}

export interface LabSet {
  id: string;
  name: string;
  lines: readonly LabSetLine[];
}

/** A LOINC code: 1–7 digits, a hyphen, one check digit. */
export const LOINC_CODE = /^\d{1,7}-\d$/;
/** A set id (`labs.set:<id>`) or a line id (`labs.<id>`). */
export const LAB_SET_ID = /^[a-z0-9][a-z0-9-]{0,39}$/;
/** Words the lab query forms themselves use (`labs.set:`, `labs.latest:`); never a set or line id.
 *  A set id MAY equal a line id (`labs.set:hscrp` the one-analyte panel, `labs.hscrp` its test): the
 *  query forms differ, so they never collide. */
export const RESERVED_LAB_WORDS: ReadonlySet<string> = new Set(['set', 'latest']);

export const LAB_SET_CATALOGUE_VERSION: string = catalogue.version;

export const LAB_SETS: readonly LabSet[] = catalogue.sets as readonly LabSet[];

const BY_ID = new Map(LAB_SETS.map((s) => [s.id, s]));
const LINE_BY_ID = new Map(LAB_SETS.flatMap((s) => s.lines.map((line) => [line.id, { set: s, line }] as const)));

export function labSet(id: string): LabSet | undefined {
  return BY_ID.get(id);
}

/** One test by its line id (`labs.<id>`), with the set it belongs to. */
export function labTest(id: string): { set: LabSet; line: LabSetLine } | undefined {
  return LINE_BY_ID.get(id);
}

/** Every problem with the catalogue's shape, empty when it is sound (the test runs it). */
export function catalogueProblems(sets: readonly LabSet[] = LAB_SETS): string[] {
  const problems: string[] = [];
  const setIds = new Set<string>();
  // A line id is a query id on its own (`labs.<id>`), so it is unique across the whole catalogue.
  const allLineIds = new Map<string, string>();
  for (const set of sets) {
    if (!LAB_SET_ID.test(set.id) || RESERVED_LAB_WORDS.has(set.id)) problems.push(`${set.id}: not a valid set id`);
    if (setIds.has(set.id)) problems.push(`${set.id}: duplicate set id`);
    setIds.add(set.id);
    if (!set.name.trim()) problems.push(`${set.id}: no name`);
    if (!set.lines.length) problems.push(`${set.id}: no lines`);
    const lineIds = new Set<string>();
    const codes = new Map<string, string>();
    for (const line of set.lines) {
      if (lineIds.has(line.id)) problems.push(`${set.id}.${line.id}: duplicate line id`);
      else if (allLineIds.has(line.id)) problems.push(`${set.id}.${line.id}: line id also used in ${allLineIds.get(line.id)}`);
      if (!LAB_SET_ID.test(line.id) || RESERVED_LAB_WORDS.has(line.id)) problems.push(`${set.id}.${line.id}: not a valid line id`);
      lineIds.add(line.id);
      if (!allLineIds.has(line.id)) allLineIds.set(line.id, set.id);
      if (!line.label.trim()) problems.push(`${set.id}.${line.id}: no label`);
      if (!line.loinc.length) problems.push(`${set.id}.${line.id}: no codes`);
      for (const code of line.loinc) {
        if (!LOINC_CODE.test(code)) problems.push(`${set.id}.${line.id}: ${code} is not a LOINC code`);
        // One code is one analyte: within a set it may stand for one line only.
        const owner = codes.get(code);
        if (owner) problems.push(`${set.id}: ${code} is on both ${owner} and ${line.id}`);
        codes.set(code, line.id);
      }
      for (const field of line.carry) {
        if (field !== 'flag' && field !== 'range') problems.push(`${set.id}.${line.id}: cannot carry "${field}"`);
      }
    }
  }
  return problems;
}
